import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Header, Icon, Modal } from 'semantic-ui-react';
import { Badge } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { swUpdate } from '../../redux';
import { Utils } from '../../services';
import config from '../../config/Config';
import i18n from '../../locales/i18n';

import { collectChangelogHighlights } from './changelogHighlights';
import { fetchYodaChangelog } from '../../services/yodaChangelog';

import './SwUpdateModal.scss';

const CHANGELOG_TIMEOUT_MS = 20000;

const fetchChangelogHighlights = async (appVersion) => {
    const result = await fetchYodaChangelog({
        projectId: config.yoda_project_id,
        mergeIds: [config.yoda_kore_project_id],
        timeoutMs: CHANGELOG_TIMEOUT_MS,
    });

    return collectChangelogHighlights(
        result.changelog || [],
        appVersion,
        result.upcoming || []
    );
};

/** Same markup/classes as the CommandPalette knowledge-base lightbox. */
const ImageLightbox = ({ src, alt, onClose }) => {
    useEffect(() => {
        if (!src)
            return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [src, onClose]);

    if (typeof document === 'undefined' || !src)
        return null;

    const caption = (alt || '').trim();

    return createPortal(
        <div
            className="CommandPaletteKbLightbox CommandPaletteKbLightboxRoot"
            role="dialog"
            aria-modal="true"
            aria-label={caption || 'Image'}
            onClick={onClose}
        >
            <figure className="CommandPaletteKbLightboxFigure">
                <img
                    src={src}
                    alt={caption ? '' : (alt || '')}
                    className="CommandPaletteKbLightboxImg"
                    draggable={false}
                />
                {caption ? (
                    <figcaption className="CommandPaletteKbLightboxCaption">
                        {caption}
                    </figcaption>
                ) : null}
            </figure>
        </div>,
        document.body
    );
};

const SlideBody = ({ slide, onOpenLightbox, releaseTypeValues }) => {
    if (slide.kind === 'info') {
        return (
            <div className="SwUpdateModal__info">
                <Icon name="info circle" size="big" className="SwUpdateModal__info-icon" />
                <h4 className="SwUpdateModal__slide-title">{slide.title}</h4>
                <p className="SwUpdateModal__slide-description">{slide.description}</p>
                {slide.others?.length > 0 && (
                    <ul className="SwUpdateModal__others">
                        {slide.others.map((item, i) => {
                            const typeMeta = releaseTypeValues[item.type];
                            return (
                                <li key={i}>
                                    {typeMeta && (
                                        <span className="SwUpdateModal__other-type">
                                            {typeMeta.icon}&nbsp;
                                            <Badge color={typeMeta.color}>{typeMeta.text}</Badge>
                                        </span>
                                    )}
                                    <span className="SwUpdateModal__other-title">
                                        {Utils.localized_or_fallback(item.title, i18n.language)}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        );
    }

    const title = Utils.localized_or_fallback(slide.title, i18n.language);

    return (
        <>
            {slide.imageUrl && (
                <button
                    type="button"
                    className="SwUpdateModal__image-btn"
                    onClick={() => onOpenLightbox(slide.imageUrl, title)}
                    aria-label={title || 'Zoom'}
                >
                    <img
                        className="SwUpdateModal__image"
                        src={slide.imageUrl}
                        alt={title || ''}
                    />
                </button>
            )}
            <h4 className="SwUpdateModal__slide-title">{title}</h4>
            <div
                className="SwUpdateModal__slide-description"
                dangerouslySetInnerHTML={{
                    __html: Utils.localized_or_fallback(slide.description, i18n.language)
                }}
            />
        </>
    );
};

const WhatsNewCarousel = ({ items, others, infoTitle, infoDescription, onOpenLightbox }) => {
    const { t } = useTranslation();
    const releaseTypeValues = useMemo(() => ({
        feature: {
            icon: <i className="fa fa-plus"></i>,
            text: t('changelog.features'),
            color: 'success'
        },
        fix: {
            icon: <i className="fa fa-bug"></i>,
            text: t('changelog.fixes'),
            color: 'warning'
        },
        i18n: {
            icon: <i className="fa fa-language"></i>,
            text: t('changelog.i18n'),
            color: 'primary'
        },
        ui: {
            icon: <i className="fa fa-paint-brush"></i>,
            text: t('changelog.ui'),
            color: 'info'
        },
        build: {
            icon: <i className="fa fa-cog"></i>,
            text: t('changelog.builds'),
            color: 'muted'
        },
    }), [t]);

    const slides = [
        ...items.map((item) => ({ kind: 'change', ...item })),
        { kind: 'info', title: infoTitle, description: infoDescription, others }
    ];
    const [index, setIndex] = useState(0);

    useEffect(() => {
        setIndex(0);
    }, [items]);

    const goPrev = () => setIndex((i) => (i === 0 ? slides.length - 1 : i - 1));
    const goNext = () => setIndex((i) => (i === slides.length - 1 ? 0 : i + 1));

    const onViewportClick = (e) => {
        if (slides.length <= 1)
            return;
        if (e.target.closest('button, a, .SwUpdateModal__others'))
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        if (x < rect.width / 2)
            goPrev();
        else
            goNext();
    };

    if (!slides.length)
        return null;

    return (
        <div className="SwUpdateModal__carousel">
            <div className="SwUpdateModal__stage">
                {slides.length > 1 && (
                    <button
                        type="button"
                        className="SwUpdateModal__nav-btn SwUpdateModal__nav-btn--prev"
                        onClick={goPrev}
                        aria-label="Previous"
                    >
                        <Icon name="chevron left" size="big" />
                    </button>
                )}
                <div
                    className="SwUpdateModal__viewport"
                    onClick={onViewportClick}
                >
                    {slides.map((slide, i) => (
                        <div
                            key={i}
                            className={`SwUpdateModal__slide${i === index ? ' is-active' : ''}`}
                            aria-hidden={i !== index}
                        >
                            <SlideBody
                                slide={slide}
                                onOpenLightbox={onOpenLightbox}
                                releaseTypeValues={releaseTypeValues}
                            />
                        </div>
                    ))}
                </div>
                {slides.length > 1 && (
                    <button
                        type="button"
                        className="SwUpdateModal__nav-btn SwUpdateModal__nav-btn--next"
                        onClick={goNext}
                        aria-label="Next"
                    >
                        <Icon name="chevron right" size="big" />
                    </button>
                )}
            </div>
            {slides.length > 1 && (
                <div className="SwUpdateModal__dots">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            className={`SwUpdateModal__dot${i === index ? ' is-active' : ''}`}
                            onClick={() => setIndex(i)}
                            aria-label={`Slide ${i + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const UpdateCta = ({ onUpdate, label }) => (
    <div className="SwUpdateModal__cta">
        <Button color="green" inverted size="huge" className="SwUpdateModal__cta-btn" onClick={onUpdate}>
            <Icon name="checkmark" />&nbsp;
            {label}
        </Button>
    </div>
);

const SwUpdateModal = () => {
    const { hasUpdate } = useSelector(state => state.worker);
    const [open, setOpen] = useState(false);
    const [highlights, setHighlights] = useState([]);
    const [others, setOthers] = useState([]);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const [lightboxAlt, setLightboxAlt] = useState('');

    useEffect(() => {
        setOpen(hasUpdate);
    }, [hasUpdate]);

    // Hide mobile nav chrome (z-index 10000) and lift the dimmer so the
    // mandatory update covers the full screen; otherwise the CTA sits below
    // the fold under the 48px strip.
    useEffect(() => {
        if (typeof document === 'undefined')
            return undefined;
        document.body.classList.toggle('sw-update-open', open);
        return () => {
            document.body.classList.remove('sw-update-open');
        };
    }, [open]);

    useEffect(() => {
        if (!hasUpdate) {
            setHighlights([]);
            setOthers([]);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const result = await fetchChangelogHighlights(config.app_version);
                if (!cancelled) {
                    setHighlights(result.highlights);
                    setOthers(result.others);
                }
            } catch (e) {
                console.log(e);
                if (!cancelled) {
                    setHighlights([]);
                    setOthers([]);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [hasUpdate]);

    const closeLightbox = useCallback(() => {
        setLightboxSrc(null);
        setLightboxAlt('');
    }, []);

    const openLightbox = useCallback((src, alt) => {
        setLightboxSrc(src);
        setLightboxAlt(alt || '');
    }, []);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const doUpdate = () => {
        dispatch(swUpdate());
    };

    const hasCarousel = highlights.length > 0;

    return (
        <>
            <Modal
                basic
                closeOnDimmerClick={false}
                closeOnDocumentClick={false}
                closeOnEscape={false}
                onClose={() => {
                    // Update is mandatory — dismissing the modal is not allowed.
                }}
                onOpen={() => setOpen(true)}
                open={open}
                size={hasCarousel ? 'fullscreen' : 'small'}
                className={`SwUpdateModal${hasCarousel ? ' SwUpdateModal--with-carousel' : ''}`}
            >
                <Header icon>
                    <Icon name="refresh" />
                    {t('app.worker.an_update_is_available')}
                </Header>
                <Modal.Content>
                    <div className="SwUpdateModal__body">
                        <p>
                            {t('app.worker.click_here_to_update')}
                        </p>
                        {hasCarousel && (
                            <WhatsNewCarousel
                                items={highlights}
                                others={others}
                                infoTitle={t('app.worker.whats_new_more_title')}
                                infoDescription={t('app.worker.whats_new_more_description')}
                                onOpenLightbox={openLightbox}
                            />
                        )}
                    </div>
                    <UpdateCta onUpdate={doUpdate} label={t('app.worker.do_update')} />
                </Modal.Content>
            </Modal>
            <ImageLightbox src={lightboxSrc} alt={lightboxAlt} onClose={closeLightbox} />
        </>
    );
};

export default SwUpdateModal;
